import Debug from 'debug';
import { QuestionAttemptService } from '@/app/question-attempt/service';
import { PracticeSessionService } from '@/app/practice-session/service';
import { PracticeStateMachineAbstractState, PracticeStateMachineCommonEvents } from './state'
import { PracticeStateMachineData,
  PracticeQuestionAnswer, PracticeQuestionEvaluationResult, PracticeQuestionState } from '../data';
import { Adaptivity } from './adaptivity'
import { QuestionStateMachine } from '@/app/question/domain/state-machine';
import { createQuestionStateMachine } from '@/app/quiz-player/api/state-machine';

const debug = Debug('practice-in-progress-state')

interface Events extends PracticeStateMachineCommonEvents {
  onFinished: () => void
}

export class PracticeInProgressState extends PracticeStateMachineAbstractState<Events> {

  public readonly name = 'in-progress'
  private currentQuestionStateMachine?: QuestionStateMachine

  constructor(
    private state: PracticeStateMachineData,
    private adaptivity: Adaptivity,
    private questionAttemptService: QuestionAttemptService,
    private practiceSessionService: PracticeSessionService) {

    super()
  }

  enter() {
    debug('enter')
    this.registerQuestionShownTime()
    
    this.createCurrentQuestionStateMachine()
  }

  cancel() {
    debug('cancel')
    this.beginCancel()
    this.endCancel()
  }

  navigate(questionIndex: number) {
    const success = this.state.updateCurrentQuestion(questionIndex, this.navigationOptions)
    if (success) {
      this.afterNavigationHook()
    }
  }

  navigateNext() {
    const success = this.state.goToNextQuestion(this.navigationOptions)
    if (success) {
      this.afterNavigationHook()
    }
  }

  navigatePrevious() {
    const success = this.state.goToPreviousQuestion(this.navigationOptions)
    if (success) {
      this.afterNavigationHook()
    }
  }

  private afterNavigationHook() {
    this.registerQuestionShownTime()
    this.updateSessionNavigation()
    this.updateSessionQuestionTimes()

    this.createCurrentQuestionStateMachine()
  }

  private registerQuestionShownTime() {
    this.state.registerQuestionShownTime(new Date())
  }

  private get navigationOptions() {
    return { updateLastIndex: true }
  }

  changeState(state: PracticeQuestionState) {

    const { stateMachine, questionIndex } = this.resolveCurrentStateMachine()

    stateMachine.updateState(state)
    this.updateSessionQuestionState(questionIndex)
  }

  changeAnswer(answer: PracticeQuestionAnswer, evaluation: PracticeQuestionEvaluationResult) {

    const { stateMachine, questionIndex } = this.resolveCurrentStateMachine()

    stateMachine.updateAnswer(answer)
    stateMachine.updateEvaluation(evaluation)

    this.state.registerQuestionAnsweredTime(new Date())

    this.updateSessionQuestionState(questionIndex)
  }

  evaluateQuestion() {
    const { stateMachine, questionIndex } = this.resolveCurrentStateMachine()

    stateMachine.evaluate()
    this.state.enableNextQuestion()
    this.state.registerQuestionFinishTime()

    this.updateSessionQuestionState(questionIndex)
    this.registerQuestionAttempt(questionIndex)
  }

  showQuestionSolution() {
    const { stateMachine, questionIndex } = this.resolveCurrentStateMachine()

    stateMachine.showSolution()
    this.updateSessionQuestionState(questionIndex)
  }

  private registerQuestionAttempt(questionIndex: number) {
    const questionTime = this.state.queryQuestionTime(questionIndex)
    const currentQuestion = this.state.questionAt(questionIndex)

    const attempt = {
      id: currentQuestion.question.id,
      attempt: {
        thinkingTimeSeconds: questionTime.seconds,
        achievedScore: currentQuestion.evaluation.details.score
      }
    }
    
    this.questionAttemptService.addQuestionAttempts([ attempt ])
  }

  resetQuestion() {

    const { stateMachine, questionIndex } = this.resolveCurrentStateMachine()
    
    stateMachine.reset()

    this.state.resetQuestionTime(new Date())
    this.updateSessionQuestionState(questionIndex)
  }

  private createCurrentQuestionStateMachine() {
    const stateMachine = this.createQuestionStateMachine(this.state.questionIndex)
    this.currentQuestionStateMachine = stateMachine
  }

  private createQuestionStateMachine(questionIndex: number) {
    const { question, state } = this.state.questionForStateMachine(questionIndex)
    return createQuestionStateMachine(question, state)
  }

  private resolveCurrentStateMachine() {
    if (!this.currentQuestionStateMachine) {
      throw new Error(`Coding error: current question state machine has not been set.`)
    }

    const questionIndex = this.state.questionIndex

    return {
      stateMachine: this.currentQuestionStateMachine,
      questionIndex: questionIndex
    }
  }

  finish() {
    debug('finish')

    const level = this.adaptivity.calculateLevel(this.state.allPracticeQuestions)
    debug('calculated after level', level)
    this.state.finished(level)

    this.finalizeSession()

    this.triggerEvent(events => events.onFinished())
  }

  exit() {
    debug('exit')
  }

  private finalizeSession() {
    this.updateSessionQuestionTimes()
    this.practiceSessionService.finishSession(this.state.currentSessionId)
  }

  private updateSessionNavigation() {
    this.practiceSessionService.updateNavigation(this.state.currentSessionId,
                                                 this.state.exportNavigationState())
  }

  private updateSessionQuestionTimes() {
    this.practiceSessionService.updateQuestionTimes(this.state.currentSessionId, {
      questionTimes: this.state.exportQuestionTimesState()
    })
  }

  private updateSessionQuestionState(questionIndex: number) {
    const state = this.state.exportQuestionStateAt2(questionIndex)
    this.practiceSessionService.updateQuestionState(this.state.currentSessionId, state)
  }
}
